/**
 * Context registration orchestration for React Query results.
 * Manages QueryCache callbacks and registers contexts with DutchieIntelligenceService.
 */

import { storeLargeQueryData } from './config';
import { getMaxContextSize, stringifyAndSize } from './helpers';
import { getContextConfig } from './query-inspection';
import { createStub } from './stub-creation';

import type { IntelligenceContextCallback } from './types';
import type { Query, QueryKey } from '@tanstack/react-query';

/**
 * Module-level callback registry.
 * This allows the QueryCache callback (in index.js) to be wired up
 * before DutchieIntelligenceContext is available.
 */
let intelligenceContextCallback: ((queryKey: QueryKey, data: unknown, meta: unknown) => void) | null = null;

/**
 * Queue for queries that completed before the callback was registered.
 * These will be processed once the callback is available.
 */
const pendingQueries: Array<{ data: unknown; meta: unknown; queryKey: QueryKey }> = [];

/**
 * Register the DutchieIntelligenceContext callback.
 * Called from a component inside DutchieIntelligenceProvider once context is available.
 */
export function registerIntelligenceContextCallback(intelligenceContext: IntelligenceContextCallback): void {
  intelligenceContextCallback = (queryKey: QueryKey, data: unknown, meta: unknown) => {
    registerQueryContext(queryKey, data, meta, intelligenceContext);
  };

  // Process any queries that completed before callback was registered
  if (pendingQueries.length > 0) {
    pendingQueries.forEach(({ queryKey, data, meta }) => {
      try {
        registerQueryContext(queryKey, data, meta, intelligenceContext);
      } catch (error) {
        console.warn('[React Query Context] Failed to process pending query:', error);
      }
    });
    pendingQueries.length = 0; // Clear the queue
  }
}

/**
 * Unregister the DutchieIntelligenceContext callback (for cleanup).
 */
export function unregisterIntelligenceContextCallback(): void {
  intelligenceContextCallback = null;
}

/**
 * Called by QueryCache.onSuccess callback in index.js.
 * Delegates to the registered callback if available.
 *
 * @param queryKey - React Query key
 * @param data - Query result data (typed as unknown by React Query at QueryCache level)
 * @param query - React Query instance
 */
export function handleQuerySuccess(queryKey: QueryKey, data: unknown, query: Query): void {
  if (intelligenceContextCallback) {
    try {
      intelligenceContextCallback(queryKey, data, query.meta);
    } catch (error) {
      console.warn('[React Query Context] Failed to handle query success:', error);
    }
  } else {
    // Callback not registered yet - queue this query for later processing
    pendingQueries.push({ queryKey, data, meta: query.meta });
  }
}

/**
 * Register a React Query result as AI context.
 * This is called internally by the registered intelligence context callback,
 * which is invoked when handleQuerySuccess processes a successful query.
 *
 * @param queryKey - React Query key
 * @param data - Query result data (unknown for type safety)
 * @param meta - Query metadata
 * @param intelligenceContext - Intelligence context API
 */
function registerQueryContext(
  queryKey: QueryKey,
  data: unknown,
  meta: unknown,
  intelligenceContext: IntelligenceContextCallback
): void {
  try {
    const config = getContextConfig(queryKey, data, meta);

    if (!config) {
      return; // Skip registration
    }

    const { id, metadata } = config;

    // Stringify and calculate size
    const { json: contextString, bytes: sizeInBytes } = stringifyAndSize(data);

    // Get max size from config or use global default
    const maxSize = getMaxContextSize(meta);

    // If context is too large, create a stub and store full data
    if (sizeInBytes > maxSize) {
      // Store full data in cache for later retrieval
      storeLargeQueryData(id, data);

      // Create stub representation
      const stub = createStub(queryKey, data, sizeInBytes);
      const { json: stubString, bytes: stubSize } = stringifyAndSize(stub);

      // Register stub with DutchieIntelligenceContext
      intelligenceContext.setComponentContext(id, stubString, metadata.scope, {
        ...metadata,
        size: stubSize, // Use stub size for metadata
      });
      return;
    }

    // Register full context with DutchieIntelligenceContext (data is small enough)
    intelligenceContext.setComponentContext(id, contextString, metadata.scope, metadata);
  } catch (error) {
    // Don't break the app if context registration fails
    console.warn('[React Query Context] Failed to register:', error);
  }
}
